import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Subject,
  Subscription,
  firstValueFrom,
  lastValueFrom,
  skip,
  take,
  takeUntil,
} from 'rxjs';
import { BackendApiService } from 'src/app/core/services/backend-api';
import {
  ApprovingOfficerSummary,
  VendorDetails,
  WorkflowHeader,
} from 'src/app/shared/models/database-models/ApprovingOfficerSummary.model';
import { Store } from '@ngrx/store';
import {
  ExtendedKeycloakUser,
  PurchaseType,
  StoreDropdowns,
  UserInformation,
} from 'src/app/shared/models/app-models';
import { CurrentWorkFlowHistory } from '../../shared/models/database-models';
import { DOTP, SaveMyPurchase } from '../../shared/models/database-models/SaveMyPurchase.model';
import { ActionEnum, WorflowHistory } from '../../shared/models/app-models/Approving.model';
import { AlertType } from 'src/app/shared/components/alert/alert.component';
import { decrementLoading, incrementLoading } from 'src/app/core/store/loading.action';
import { LoadingState } from 'src/app/core/store/loading.reducer';

interface StatusData {
  requestingOfficer: string;
  status: string;
  subStatus: string;
  submittedOn: Date | null;
  documentNo: string;
  purchaseType: string;
}

type ActionType = 'approve' | 'reject' | 'recommend' | 'endorse' | 'release';
type ApprovalType = 'budget' | 'vendor' | 'sow' | 'woc' | 'tender' | 'dotp';

interface Action {
  status: 'pending' | 'taken' | null;
  actionType: ActionType;
  budgetType: string;
  purchaseType: PurchaseType;
  approvalType: ApprovalType;
}

@Component({
  selector: 'app-approving-officer-page',
  templateUrl: './approving-officer-page.component.html',
  styleUrls: ['./approving-officer-page.component.scss'],
})
export class ApprovingOfficerPageComponent implements OnDestroy, OnInit {
  isNotExcoBoard: boolean = true;
  currentRoute: string[] = ['My Worklist'];
  breadCrumbFlow:string[]=['Purchase Modes'];
  currentPageString: string = '';
  activePage: string = '';
  budgetType: string = '';
  dagRunId: string = '';
  approvingOfficerSummary!: ApprovingOfficerSummary;
  mpDocumentDraftDetails!: SaveMyPurchase;
  currentWorkflow!: WorkflowHeader;
  loggedInUsername: string = '';
  loggedInUserfullname: string = '';
  statusData: StatusData = {
    requestingOfficer: '',
    status: '',
    subStatus: '',
    submittedOn: null,
    documentNo: '',
    purchaseType: '',
  };

  actionTaken: Action = {
    status: null,
    budgetType: '',
    purchaseType: PurchaseType.RFQ,
    approvalType: 'budget',
    actionType: 'approve',
  };

  actionTakenMap = {
    approve: 'approved',
    reject: 'rejected',
    recommend: 'recommended',
    endorse: 'endorsed',
    release: 'released',
  };

  destroy$: Subject<boolean> = new Subject<boolean>();

  userList: ExtendedKeycloakUser[] = [];
  userAction: { message: string; taken: boolean } = {
    message: '',
    taken: false,
  };

  messagePieces = {
    pending: {
      first: '',
      second: '',
      third: ''
    },
    taken: {
      first: '',
      second: '',
      third: '',
    },
  };

  actions: Action[] = []
  excoBoardMessage?: string;
  private loadingSubscription: Subscription = new Subscription();
  isApprovingOverviewLoading=false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private BackendApi: BackendApiService,
    private store: Store<{
      dp: StoreDropdowns; user: UserInformation; loading: LoadingState
    }>
  ) {
      this.loadingSubscription = this.store.select('loading').subscribe((loadingState) => {
      this.isApprovingOverviewLoading = loadingState.loadingStates['approving-overview'] > 0;
    });
    const previousPageName = sessionStorage.getItem('fromPage');
    if (previousPageName) {
      this.breadCrumbFlow = [previousPageName];
    } 
    this.currentPageString = this.route.snapshot.params['id'] as string;
    this.activePage=this.currentPageString;
    this.dagRunId = this.route.snapshot.params['dagId'] as string;
    this.store
      .select('user')
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: UserInformation) => {
        this.loggedInUsername = user.userName;
        this.loggedInUserfullname = user.fullName;
    });
    this.store
      .select('dp')
      .pipe(takeUntil(this.destroy$))
      .subscribe((dp: StoreDropdowns) => {
        this.userList = dp.UserList as ExtendedKeycloakUser[];
      });
  }
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  async ngOnInit(): Promise<void> {
    try {
      if (!this.userList.length) {
        this.userList = (
          await firstValueFrom(this.store.select('dp').pipe(skip(1)))
        ).UserList as ExtendedKeycloakUser[];
        console.log(this.userList);
      }
      this.getMPDocumentDetails(this.currentPageString);
      this.getMPDocumentDraft();
    } catch (e: any) {
      console.log('error retrieving data ', e);
    }
  }

  returnToWorkList = (args: any): void => {
    this.router.navigateByUrl('/worklist');
  };
  generateSubStatus(isDOTP: boolean, status: string) {
    if (isDOTP) {
      if (status.toLowerCase().includes('reject')) {
        return 'WOC-DOTP Rejected'
      } if (status.toLowerCase().includes('approve')) {
        return 'Pending Approval'
      }
    }
    return status
  }
  generateStatus(isDOTP: boolean, status: string) {
    if (isDOTP && (status.toLowerCase().includes('reject') || status.toLowerCase().includes('approve'))) {
      return 'Pending Approval'
    }
    return status
  }
  async getMPDocumentDetails(mpDocumentNumber: string) {
    try {
      this.store.dispatch(incrementLoading({ context: 'approving-overview' }));
      let data: ApprovingOfficerSummary = await lastValueFrom(
        this.BackendApi.getAOSummary(mpDocumentNumber)
      );
      this.statusData = {
        requestingOfficer:
          this.userList.find((user) => user.id === data.RequestingOfficer)
            ?.last_name ?? '',
        documentNo: data.MPDocumentNumber,
        status: this.generateStatus(this.approvingOfficerSummary?.purchaseType === 'WOC-DOTP', data.documentMainStatus),
        subStatus: this.generateSubStatus(this.approvingOfficerSummary?.purchaseType === 'WOC-DOTP', data.documentSubStatus),
        purchaseType: data.purchaseType,
        submittedOn: data.CreatedOn,
      };
      // find all the vendors
      let VendorDetails: VendorDetails[] = await lastValueFrom(
        this.BackendApi.getVendorsByMPNumber(mpDocumentNumber)
      );
      data['mpVendorDetails'] = VendorDetails;
      // find the current workflow that is selected
      this.currentWorkflow = await lastValueFrom(
        this.BackendApi.getAOWorkFlow(mpDocumentNumber, this.dagRunId)
      );

      if(!await this.checkAOAccess(this.currentWorkflow)) {
        this.router.navigateByUrl('/unauthorized');
      }
      
      if(!this.currentWorkflow.isActive){
        this.navigateToNotFound();
      }
      switch (this.currentWorkflow.workflow_id) {
        case WorflowHistory.CARApproval:
        case WorflowHistory.OPSApproval:
        case WorflowHistory.CAPAUCApproval:
        case WorflowHistory.RESApproval:
          this.actionTaken = this.actions.find((i) => !!i.budgetType) as Action;
          break;
        case WorflowHistory.WOCApproval:
        case WorflowHistory.DOTPApproval:
          this.actionTaken = this.actions.find(
            (i) => !i.budgetType && i.approvalType === 'woc'
          ) as Action;
          break;
        case WorflowHistory.SOWApproval:
          this.actionTaken = this.actions.find(
            (i) => !i.budgetType && i.approvalType === 'sow'
          ) as Action;
          break;
        case WorflowHistory.TenderApproval:
          this.actionTaken = this.actions.find(
            (i) => !i.budgetType && i.approvalType === 'tender'
          ) as Action;
          break;
        case WorflowHistory.VendorDocumentApproval:
          this.actionTaken = this.actions.find(
            (i) => !i.budgetType && i.approvalType === 'vendor'
          ) as Action;
          break;
        default:
          this.actionTaken = this.actions[0];
      }
      this.generateMessagePieces();
      this.generateExcoBoardMessage(this.currentWorkflow);
      this.approvingOfficerSummary = data;
    } catch (e) {
      this.navigateToNotFound();
      console.log("Unable to load MyPurchase Details for AO");
      //throw e;
    } finally {
      this.store.dispatch(decrementLoading({ context: 'approving-overview' }));
    }
  }

  async getMPDocumentDraft() {
    try {
      this.store.dispatch(incrementLoading({ context: 'approving-overview' }));
      let data = await lastValueFrom(this.BackendApi.getDraft(this.currentPageString));
      this.mpDocumentDraftDetails = data;
    } catch (e) {
      console.log("Unable to get MyPurchase Draft");
    } finally {
      this.store.dispatch(decrementLoading({ context: 'approving-overview' }));
    }
  }

  async checkAOAccess(workflow: WorkflowHeader): Promise<boolean> {
    let cos = await this.getCoveringofficer(workflow);

    const actioners = Array.from(new Set(
      workflow.workflowHistory.flatMap(history => [
        history.CoveringActionerName,
        history.OriginalActionerName
      ]).filter(Boolean)
    ));

    if (actioners.includes(this.loggedInUserfullname)) {
      return true;
    }

    if (cos) {
      const cosProperties = [
        'approver', 'approved', 'recommender', 'recommended',
        'releaser', 'endorser', 'endorsed'
      ];

      for (const prop of cosProperties) {
        if ((cos as any)[prop]?.includes(this.loggedInUsername)) {
          return true;
        }
      }
    }

    return false;
  }

  async getCoveringofficer(data: WorkflowHeader) {
    try {
      let approver: string[] = [];
      let recommender: string[] = [];
      let endorser: string[] = [];
      let endorsed: string[] = [];
      let releaser: string[] = [];
      let approved: string[] = [];
      let recommended: string[] = [];
      if (data.approver) {
        for (let actioner of data.approver.split(',')) {
          if (actioner !== null && actioner !== '' && actioner !== undefined) {
            approver.push(actioner);
          }
        }
      }
      if (data.recommender) {
        for (let actioner of data.recommender.split(',')) {
          if (actioner !== null && actioner !== '' && actioner !== undefined) {
            recommender.push(actioner);
          }
        }
      }
      if (data.endorser) {
        for (let actioner of data.endorser.split(',')) {
          if (actioner !== null && actioner !== '' && actioner !== undefined) {
            endorser.push(actioner);
          }
        }
      }
      if (data.endorsed) {
        for (let actioner of data.endorsed.split(',')) {
          if (actioner !== null && actioner !== '' && actioner !== undefined) {
            endorsed.push(actioner);
          }
        }
      }
      if (data.releaser) {
        for (let actioner of data.releaser.split(',')) {
          if (actioner !== null && actioner !== '' && actioner !== undefined) {
            releaser.push(actioner);
          }
        }
      }
      if (data.approved) {
        for (let actioner of data.approved.split(',')) {
          if (actioner !== null && actioner !== '' && actioner !== undefined) {
            approved.push(actioner);
          }
        }
      }
      if (data.recommended) {
        for (let actioner of data.recommended.split(',')) {
          if (actioner !== null && actioner !== '' && actioner !== undefined) {
            recommended.push(actioner);
          }
        }
      }

      return await lastValueFrom(this.BackendApi.getCoveringOfficers({
        approver: approver,
        recommender: recommender,
        endorser: endorser,
        endorsed: endorsed,
        releaser: releaser,
        approved: approved,
        recommended: recommended
      }));
    }
    catch (err) {
      console.warn('Error in fetching the getCoveringOfficers ', err);
    }
    return;
  }

  generateExcoBoardMessage(workflow: WorkflowHeader) {
    const myMap = new Map<WorflowHistory, string>([
      [WorflowHistory.OPSApproval, "operating"],
      [WorflowHistory.CAPAUCApproval, "capital AUC"],
      [WorflowHistory.CARApproval, "capital"]
    ]);
    if ((workflow.workflow_id == WorflowHistory.OPSApproval || workflow.workflow_id == WorflowHistory.CAPAUCApproval || workflow.workflow_id == WorflowHistory.CARApproval) && workflow.isEXCOBoard) {
      if (this.currentWorkflow.statusCode == '8' || this.currentWorkflow.statusCode == '6') {
        this.excoBoardMessage = `Please review the <b>approval of requirements</b> for the <b>${myMap.get(workflow.workflow_id)} budget </b> on behalf of EXCO/Board for this request.`;
        this.isNotExcoBoard = false;
      }
      else if (this.currentWorkflow.statusCode == '1') {
        this.excoBoardMessage = `You have approved the <b> approval of requirements</b> for the <b>${myMap.get(workflow.workflow_id)} budget </b> budget on behalf of EXCO/Board for this request.`;
        this.isNotExcoBoard = false;
      }
      else if (this.currentWorkflow.statusCode == '2') {
        this.excoBoardMessage = `You have rejected the <b> approval of requirements</b> for the <b>${myMap.get(workflow.workflow_id)} budget </b> budget on behalf of EXCO/Board for this request.`;
        this.isNotExcoBoard = false;
      }
      else this.excoBoardMessage = undefined;
    }
  }
  getActionType(type: string) {
    const actionType = type.toLowerCase();
    if (actionType.includes('reject') || actionType.includes('not')) {
      return 'reject';
    }
    if (actionType.includes('approv')) {
      return 'approve';
    }

    if (actionType.includes('recomme')) {
      return 'recommend';
    }
    if (actionType.includes('endorse')) {
      return 'endorse';
    }
    if (actionType.includes('release')) {
      return 'release';
    }
    return '';
  }
  getActionState(action: string): { taken: boolean, type: ActionType } {
    const actionStateMap: Record<
      string,
      { taken: boolean; type: ActionType }
    > = {
      [ActionEnum.PENDING_RECOMMENDATION]: {
        taken: false,
        type: 'recommend',
      },
      [ActionEnum.APPROVED]: {
        taken: true,
        type: 'approve',
      },
      [ActionEnum.REJECTED]: {
        taken: true,
        type: 'reject',
      },
      [ActionEnum.RECOMMENDED]: {
        taken: true,
        type: 'recommend',
      },
      [ActionEnum.NOT_RECOMMENDED]: {
        taken: true,
        type: 'reject',
      },
      [ActionEnum.PENDING_APPROVAL]: {
        taken: false,
        type: 'approve',
      },
      [ActionEnum.PENDING_ENDORSEMENT]: {
        taken: false,
        type: 'endorse',
      },
      [ActionEnum.ENDORSED]: {
        taken: true,
        type: 'endorse',
      },
      [ActionEnum.NOT_ENDORSED]: {
        taken: true,
        type: 'reject',
      },
      [ActionEnum.PENDING_RELEASE]: {
        taken: false,
        type: 'release',
      },
      [ActionEnum.RELEASED]: {
        taken: true,
        type: 'release',
      },
      [ActionEnum.NOT_RELEASED]: {
        taken: true,
        type: 'reject',
      },
      [ActionEnum.PENDING_PRIOR_APPROVAL]: {
        taken: false,
        type: 'approve',
      },
    };

    if (action === ActionEnum.SKIPPED_RECOMMENDATION) {
      return {
        taken: false,
        type: 'approve'
      }
    }

    return actionStateMap[action]
  }

  async getUserAction(histories: CurrentWorkFlowHistory) {

    console.log("history", histories);

    if (histories.budget?.length) {
      const history = histories.budget[0];
      const actioner = history.recordList[0];
      const actionState = this.getActionState(actioner.Action);
      this.actions.push({
        status: !actionState.taken ? 'pending' : 'taken',
        actionType: actionState.type,
        budgetType: history.type,
        purchaseType: this.statusData.purchaseType as PurchaseType,
        approvalType: 'budget'
      });
    }
    if (histories.woc?.length) {
      const actioner = histories.woc[0];
      const actionState = this.getActionState(actioner.Action);
      this.actions.push({
        status: !actionState.taken ? 'pending' : 'taken',
        actionType: actionState.type,
        budgetType: '',
        purchaseType: this.statusData.purchaseType as PurchaseType,
        approvalType: 'woc'
      });
    }
    if (histories.sow?.length) {
      const actioner = histories.sow[0];
      const actionState = this.getActionState(actioner.Action);
      this.actions.push({
        status: !actionState.taken ? 'pending' : 'taken',
        actionType: actionState.type,
        budgetType: '',
        purchaseType: this.statusData.purchaseType as PurchaseType,
        approvalType: 'sow'
      });
    }

    if (histories.tenderDocumentApproval?.length) {
      const actioner = histories.tenderDocumentApproval[0];
      const actionState = this.getActionState(actioner.Action);
      this.actions.push({
        status: !actionState.taken ? 'pending' : 'taken',
        actionType: actionState.type,
        budgetType: '',
        purchaseType: this.statusData.purchaseType as PurchaseType,
        approvalType: 'tender'
      });
    }

    if (histories.vendor?.length) {
      const actioner = histories.vendor[0];
      const actionState = this.getActionState(actioner.Action);
      this.actions.push({
        status: !actionState.taken ? 'pending' : 'taken',
        actionType: actionState.type,
        budgetType: '',
        purchaseType: this.statusData.purchaseType as PurchaseType,
        approvalType: 'vendor'
      });
    }
    // await this.getMPDocumentDetails(this.currentPageString);
  }

  generateMessagePieces() {
    this.isNotExcoBoard = true;
    if (this.actionTaken?.status === 'pending') {
      let first = 'approval of requirements';
      let second = '';
      let third = 'on this request'

      if (this.actionTaken.approvalType === 'sow') {
        first = 'approval of scope of work';
      }
      if (this.actionTaken.approvalType === 'tender') {
        first = 'approval of Tender documents';
      }
      if (this.actionTaken.approvalType === 'woc') {
        first = 'approval of waiver of competition';
        third = 'for this purchase';
        if (this.approvingOfficerSummary?.purchaseType === 'WOC-DOTP') {
          first = 'approval of WOC/DOTP'
        }
      }
      if (this.actionTaken.approvalType === 'vendor') {
        first = 'approval of vendor document(s)'
      }
      if (
        this.actionTaken.approvalType === 'budget'
      ) {
        if (
          [WorflowHistory.CAPAUCApproval, WorflowHistory.CARApproval].includes(
            this.currentWorkflow.workflow_id as WorflowHistory
          ) && this.actionTaken.actionType === 'recommend'
        ) {
          if(this.currentWorkflow.isSpaceDRO === true){
            first = "recommendation of space requirements"
          }
        }
        second = `${this.actionTaken.budgetType} budget`
      }

      // if (){}
      this.messagePieces.pending = {
        first,
        second,
        third
      };
    }

    if (this.actionTaken?.status === 'taken') {
      let first = '';
      let second = '';
      let third = 'approval of requirements';
      if (this.actionTaken.approvalType === 'sow') {
        first = this.actionTakenMap[this.actionTaken.actionType]
        third = 'scope of work';
      }
      if (this.actionTaken.approvalType === 'tender') {
        first = this.actionTakenMap[this.actionTaken.actionType]
        third = 'Tender Documents'
      }
      if (this.actionTaken.approvalType === 'woc') {
        first = this.actionTakenMap[this.actionTaken.actionType]
        second = 'waiver of competition'
        if (this.approvingOfficerSummary?.purchaseType === 'WOC-DOTP') {
          second = 'approval of WOC/DOTP'
        }
        third = 'for this purchase'

      }
      if (this.actionTaken.approvalType === 'vendor') {
        first = this.actionTakenMap[this.actionTaken.actionType]
        third = 'vendor document(s)'
      }
      if (this.actionTaken.approvalType === 'budget') {
        first = this.actionTakenMap[this.actionTaken.actionType];
        if (
          [WorflowHistory.CAPAUCApproval, WorflowHistory.CARApproval].includes(
            this.currentWorkflow.workflow_id as WorflowHistory
          ) && this.actionTaken.actionType === 'recommend'
        ) {
          if(this.currentWorkflow.isSpaceDRO === true){
            third = "space requirements"
          }
        }
      }
      this.messagePieces.taken = {
        first,
        second,
        third
      };
    }
  }

  navigateToRO(DocumentNo: string, purchaseType: string) {
    let url: string = '';
    switch (purchaseType) {
      case PurchaseType.RFQ:
        url = '/purchase/rfq/' + `${DocumentNo}`;
        break;
      case PurchaseType.TENDER:
        url = '/purchase/tender/' + `${DocumentNo}`;
        break;
      case PurchaseType.ONLINE:
        url = '/purchase/corporatecard/' + `${DocumentNo}`;
        break;
      case PurchaseType.PERMISSIBLE_COVERING:
        url = '/purchase/permissiblecoveringpurchaseorder/' + `${DocumentNo}`;
        break;
      case PurchaseType.DC:
        url = '/purchase/dc/' + `${DocumentNo}`;
        break;
      case PurchaseType.CORPORATE_CARD:
        url = '/purchase/corporatecard/' + `${DocumentNo}`;
        break;
      case PurchaseType.PM:
        url = '/purchase/permissiblecoveringpurchaseorder/' + `${DocumentNo}`;
        break;
    }
    window.open(url, '_blank');
  }

  navigateToNotFound(){
    let url : string = '/notfound'
    window.open(url,"_self");
  }

  public get alertType(): typeof AlertType {
    return AlertType;
  }
}
